import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as Sidebar from '../layout/sidebar/components'
import { NoDataPlaceHolder } from '../PatientFolder/behavioral/components'
import { PlainButton } from '../shared/Button'
import { toastSlicer } from '../shared/toast-stack'

import { loadMoreHistory, markRead } from './actions'

interface Toast {
  type: string
  id: string
  payload: MessagePayload
}

interface State {
  notificationsHistory: NotificationHistoryState
}

interface NotificationHistoryState {
  notifications: Toast[]
  loading: boolean
  loadMoreVisible: boolean
  lastRequestedAt?: Date
}

interface NotificationsHistoryProps {
  sendMessage: (message: string) => void
}

let significantTimeThreshold = 1000 * 60

export function NotificationsHistory ({ sendMessage }: NotificationsHistoryProps): JSX.Element {
  let notificationHistory = useSelector<State, NotificationHistoryState>(state => state.notificationsHistory)
  let { notifications, loading, loadMoreVisible, lastRequestedAt } = notificationHistory
  let dispatch = useDispatch()

  const onLoadMore = useCallback((): void => {
    // @ts-expect-error // it's okay
    dispatch(loadMoreHistory())
  }, [ dispatch ])

  let handleClose = useCallback((id: string): void => {
    // @ts-expect-error // it's okay
    dispatch(markRead(id))
  }, [ dispatch ])

  let hasNotifications = notifications.length > 0

  let requestedSignificantTimeAgo =
    !lastRequestedAt ||
    new Date().valueOf() - lastRequestedAt.valueOf() > significantTimeThreshold

  let shouldAutoLoad = !hasNotifications && !loading && requestedSignificantTimeAgo

  useEffect(() => {
    if (shouldAutoLoad) {
      onLoadMore()
    }
  }, [ shouldAutoLoad, onLoadMore ])

  return (
    <Body
      notifications={notifications}
      sendMessage={sendMessage}
      loadMoreHistory={onLoadMore}
      loading={loading}
      loadMoreVisible={loadMoreVisible}
      close={handleClose}
    />
  )
}

interface Attachment {
  filename: string
  id: string
  mime_type: string
  size: bigint
  url: string
}

interface MessagePayload {
  message?: {
    author: string
    created_at: string
    body: string
    attachments: Attachment[]
  }
  chat?: {
    id: string
    participants: Array<{
      id: string
      resourceType: string
    }>
    meta: {
      patient: {
        id: string
      }
    }
  }
  clientName?: string
}

interface RenderToastProps {
  close: (id: string) => void
  toast: Toast
}

interface TempToast extends Toast {
  author?: {
    id: string
    resourceType: string
  }
  createdAt?: string
  message?: string
  attachmentName?: string | null
  patientId?: string
  clientName?: string
  chatId?: string
  place?: string
}

function RenderToast ({ close, toast }: RenderToastProps): JSX.Element | null {
  let tempToast: TempToast = { ...toast }
  if (toast.type === 'chat_message') {
    const {
      id: tId,
      payload: { message, chat, clientName }
    } = tempToast

    const patientId = chat?.meta.patient.id
    const chatId = chat?.id
    const authorResourceType = message?.author.split('/')[0]
    const authorId = message?.author.split('/')[1]
    const attachment = message?.attachments?.[0]
    const authorResource = chat?.participants?.find(pt => pt.id === authorId && pt.resourceType === authorResourceType)

    if (!authorResource || !message) {
      return null
    }

    tempToast = {
      payload: toast?.payload ?? {},
      id: tId,
      type: 'message',
      author: authorResource,
      createdAt: message.created_at ?? '',
      message: message.body ?? '',
      attachmentName: attachment?.filename ?? null,
      patientId: patientId ?? '',
      clientName: clientName ?? '',
      chatId
    }
  } else if (tempToast.type === 'alert_escalated') {
    tempToast.type = 'alert'
  } else if (tempToast.type === 'alert_owned') {
    tempToast.type = 'alert'
  }

  tempToast.place = 'sidebar'

  return toastSlicer(tempToast, tempToast.id, close)
}

interface BodyProps {
  sendMessage: (message: string) => void
  notifications: Toast[]
  loading: boolean
  loadMoreHistory: () => void
  loadMoreVisible: boolean
  close: (id: string) => void
}

function Body ({ notifications, close, loadMoreHistory }: BodyProps): JSX.Element {
  const bodyRef = useRef<HTMLDivElement>(null)

  function scrollDown (): void {
    if (!bodyRef.current) {
      return
    }

    bodyRef.current.scrollTop = bodyRef.current?.scrollHeight
  }

  useEffect(() => {
    scrollDown()
  }, [])

  return (
    <div
      ref={bodyRef}
      className='flex flex-col justify-center'
    >
      <Sidebar.Cards>
        {notifications.map((notification) => (
          <Sidebar.Card
            key={notification.id}
            border={notification.type === 'alert_escalated' ? 'red' : undefined}
          >
            <RenderToast
              key={notification.id}
              close={close}
              toast={notification}
            />
          </Sidebar.Card>
        ))}
      </Sidebar.Cards>

      {notifications.length <= 0 && (
        <NoDataPlaceHolder title='There are no Notifications to display' />
      )}

      {notifications.length > 0 && (
        <div style={{ margin: '20px 0 40px', textAlign: 'center' }}>
          <PlainButton
            btnType='plain'
            color='primary'
            label='Load More'
            onClick={loadMoreHistory}
          />
        </div>
      )}
    </div>
  )
}
